import React, { useRef, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useClinic } from '../contexts/ClinicContext';

// Component for printing case sheets
const CaseSheetPrint = ({ appointment, onClose }) => {
  const printRef = useRef();
  const { clinic_name, logo_url, address, phone, email, letterhead_url } = useClinic();
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadingImages, setLoadingImages] = useState(true);

  // Function to calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return "--";
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  // Format ID with leading zeroes to ensure 6 digits
  const formatId = (id) => {
    return String(id).padStart(6, '0');
  };

  // FIXED: Preload all images before allowing print
  useEffect(() => {
    const preloadImages = async () => {
      const imagesToLoad = [];
      
      // Add letterhead image if it exists
      if (letterhead_url) {
        imagesToLoad.push(letterhead_url);
      }
      
      // Add logo image if it exists
      if (logo_url) {
        imagesToLoad.push(logo_url);
      }

      // Add dental chart images
      imagesToLoad.push('/uploads/dental-chart-1.png');
      imagesToLoad.push('/uploads/dental-chart-2.png');

      if (imagesToLoad.length === 0) {
        setImagesLoaded(true);
        setLoadingImages(false);
        return;
      }

      try {
        // Create promises for all image loading
        const imagePromises = imagesToLoad.map(src => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(src);
            img.onerror = () => {
              console.warn(`Failed to load image: ${src}`);
              resolve(src); // Resolve anyway to not block the process
            };
            img.src = src;
          });
        });

        // Wait for all images to load
        await Promise.all(imagePromises);
        
        // Additional delay to ensure images are fully rendered
        setTimeout(() => {
          setImagesLoaded(true);
          setLoadingImages(false);
        }, 500);
        
      } catch (error) {
        console.error('Error preloading images:', error);
        setImagesLoaded(true);
        setLoadingImages(false);
      }
    };

    preloadImages();
  }, [letterhead_url, logo_url]);

  // FIXED: Only trigger print when images are loaded
  const handlePrint = async () => {
    if (!imagesLoaded) {
      console.log('Images not loaded yet, waiting...');
      return;
    }

    // Additional safety delay
    await new Promise(resolve => setTimeout(resolve, 300));

    const printStyles = `
      @page { size: A4; margin: 0; }
      body { 
        margin: 0; 
        padding: 0; 
        background-image: url('${letterhead_url}'); 
        background-size: cover; 
        background-repeat: no-repeat; 
        background-position: top left;
        -webkit-print-color-adjust: exact;
        color-adjust: exact;
      }
      @media print {
        body * { visibility: hidden; }
        #printable-case-sheet, #printable-case-sheet * { visibility: visible; }
        #printable-case-sheet { 
          position: absolute; 
          left: 0; 
          top: 0; 
          padding-top: 120px; 
          padding-bottom: 100px; 
          width: 100%; 
        }
        .print-page { 
          page-break-after: always; 
          position: relative; 
        }
        .print-page:last-child { page-break-after: avoid; }
        .no-print { display: none !important; }
        .bg-white { 
          background: transparent !important; 
          box-shadow: none !important; 
        }
        .content-container { background-color: transparent !important; }
        .semi-transparent { background-color: rgba(255, 255, 255, 0.75) !important; }
        .full-transparent { background: transparent !important; }
        .table-header { background-color: rgba(243, 244, 246, 0.85) !important; }
        .case-sheet-footer { 
          position: fixed; 
          bottom: 115px; 
          left: 40px; 
          right: 40px; 
          padding: 0; 
          text-align: left; 
          border-top: 1px solid #e5e7eb; 
          background-color: transparent !important; 
        }
        
        /* FIXED: Ensure images print correctly */
        img {
          -webkit-print-color-adjust: exact;
          color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `;

    const style = document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = printStyles;
    document.head.appendChild(style);

    // FIXED: Additional delay before opening print dialog
    setTimeout(() => {
      window.print();
      
      // Clean up
      setTimeout(() => {
        document.head.removeChild(style);
        onClose();
      }, 1000);
    }, 200);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">Case Sheet Preview</h2>
          <div className="flex space-x-3">
            {/* FIXED: Show loading state and disable print until images are loaded */}
            <button
              onClick={handlePrint}
              disabled={loadingImages || !imagesLoaded}
              className={`px-4 py-2 rounded-lg transition-colors ${
                loadingImages || !imagesLoaded
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-maroon-600 text-white hover:bg-maroon-700'
              }`}
            >
              {loadingImages ? 'Loading Images...' : 'Print'}
            </button>
            <button
              onClick={onClose}
              className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>

        {/* FIXED: Show loading indicator while images are loading */}
        {loadingImages && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-blue-700">Loading images for print...</span>
            </div>
          </div>
        )}

        {/* Printable Case Sheet */}
        <div id="printable-case-sheet" ref={printRef}>
          <div className="print-page relative bg-white p-8 shadow-lg mb-6">
            <div className="content-container relative z-10">
              {/* FIXED: Preload letterhead image in a hidden element */}
              {letterhead_url && (
                <img 
                  src={letterhead_url} 
                  alt="Letterhead" 
                  style={{ display: 'none' }}
                  onLoad={() => console.log('Letterhead preloaded')}
                  onError={() => console.warn('Letterhead failed to load')}
                />
              )}

              {/* Preload dental chart images */}
              <img 
                src="/uploads/dental-chart-1.png" 
                alt="Dental Chart 1" 
                style={{ display: 'none' }}
                onLoad={() => console.log('Dental chart 1 preloaded')}
                onError={() => console.warn('Dental chart 1 failed to load')}
              />
              <img 
                src="/uploads/dental-chart-2.png" 
                alt="Dental Chart 2" 
                style={{ display: 'none' }}
                onLoad={() => console.log('Dental chart 2 preloaded')}
                onError={() => console.warn('Dental chart 2 failed to load')}
              />

              {/* Header - Only show if no letterhead */}
              {!letterhead_url && (
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center">
                    {logo_url && (
                      <img 
                        src={logo_url} 
                        alt="Logo" 
                        className="h-16 mr-4"
                        onLoad={() => console.log('Logo loaded')}
                        onError={() => console.warn('Logo failed to load')}
                      />
                    )}
                    <div>
                      <h1 className="text-2xl font-bold text-gray-800">{clinic_name}</h1>
                      <p className="text-gray-600">{address}</p>
                      <p className="text-gray-600">Phone: {phone} | Email: {email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <h2 className="text-xl font-bold text-maroon-600">CASE SHEET</h2>
                    <p className="text-gray-600">#{formatId(appointment.id)}</p>
                    <p className="text-gray-600">{format(new Date(appointment.datetime_start), 'dd/MM/yyyy')}</p>
                  </div>
                </div>
              )}

              {/* If letterhead exists, just show a minimal header with case sheet number and date */}
              {letterhead_url && (
                <div className="flex justify-between items-center mb-2">
                  <div className="text-left semi-transparent px-4 py-2 rounded flex flex-col justify-center">
                    <h2 className="text-4xl font-bold text-maroon-600 leading-tight">Patient # {formatId(appointment.patient_id)}</h2>
                  </div>
                  <div className="text-right semi-transparent px-4 py-2 rounded">
                    <h2 className="text-2xl font-bold text-maroon-600">CASE SHEET</h2>
                    <p className="text-gray-600">Appointment Time: {format(new Date(appointment.datetime_start), 'dd/MM/yyyy HH:mm')}</p>
                  </div>
                </div>
              )}

              {/* Patient & Appointment Information */}
              <div className="mb-2">
                <table className="w-full border-collapse table-fixed text-xs ml-[2px]">
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 p-1 font-bold semi-transparent w-[10%]">Name</td>
                      <td className="h-8 whitespace-nowrap overflow-hidden truncate border border-gray-300 p-1 semi-transparent text-center" colSpan={3}>{appointment.patient_name}</td>
                      <td className="border border-gray-300 p-1 font-bold semi-transparent w-[7%]">DOB</td>
                      <td className="border border-gray-300 p-1 semi-transparent w-[15%] text-center">
                        {appointment.patient_dob ? format(new Date(appointment.patient_dob), 'dd/MM/yyyy') : 'N/A'}
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 p-1 font-bold semi-transparent">Gender</td>
                      <td className="border border-gray-300 p-1 semi-transparent text-center">{appointment.patient_sex || 'N/A'}</td>
                      <td className="border border-gray-300 p-1 font-bold semi-transparent w-[15%] text-center">QID</td>
                      <td className="border border-gray-300 p-1 semi-transparent text-center">{appointment.patient_national_id || 'N/A'}</td>
                      <td className="border border-gray-300 p-1 font-bold semi-transparent">Age</td>
                      <td className="border border-gray-300 p-1 semi-transparent text-center">{calculateAge(appointment.patient_dob)}</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 p-1 font-bold semi-transparent">Phone</td>
                      <td className="border border-gray-300 p-1 semi-transparent text-center">{appointment.contact_number || 'N/A'}</td>
                      <td className="border border-gray-300 p-1 font-bold semi-transparent w-[15%] text-center">Nationality</td>
                      <td className="border border-gray-300 p-1 semi-transparent text-center">{appointment.nationality || 'N/A'}</td>
                      <td className="border border-gray-300 p-1 font-bold semi-transparent">Dr.</td>
                      <td className="border border-gray-300 p-1 semi-transparent text-center">{appointment.doctor_name || 'N/A'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Vital Signs */}
              <div className="mb-2">
                <div className="border border-gray-300 p-3 rounded semi-transparent ml-[2px]">
                  <div className="flex flex-wrap gap-6 items-center">
                    <div className="flex items-center space-x-2">
                      <input type="checkbox" className="h-4 w-4 border-gray-300 rounded" />
                      <label className="text-sm text-gray-700">BP: ___________________</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input type="checkbox" className="h-4 w-4 border-gray-300 rounded" />
                      <label className="text-sm text-gray-700">Sugar: ___________________</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-700">Allergy: __________________________________</label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chief Complaint */}
              <div className="mb-2">
                <h3 className="text-sm font-bold mb-1 full-transparent inline-block px-2">Chief Complaint</h3>
                <div className="border border-gray-300 p-3 rounded full-transparent min-h-[50px] ml-[2px]">
                  {/* Empty space for writing */}
                </div>
              </div>

              {/* Medical History */}
              <div className="mb-2">
                <h3 className="text-sm font-bold mb-1 full-transparent inline-block px-2">Past Medical / Dental History</h3>
                <div className="border border-gray-300 p-3 rounded full-transparent min-h-[40px] ml-[2px]">
                  {/* Empty space for writing */}
                </div>
              </div>

              {/* Oral Examination */}
              <div className="mb-2">
                <h3 className="text-sm font-bold mb-1 full-transparent inline-block px-2">Oral Examination</h3>
                <div className="full-transparent">
                  {/* Dental Charts */}
                  <div className="grid grid-cols-[repeat(20,minmax(0,1fr))] h-[450px]">
                    <div className="col-span-11 flex items-center justify-end">
                      <img 
                        src="/uploads/dental-chart-1.png" 
                        alt="Adult Dental Chart" 
                        className="h-[395px] object-contain"
                        onLoad={() => console.log('Dental chart 1 loaded in print')}
                        onError={() => console.warn('Dental chart 1 failed to load in print')}
                      />
                    </div>
                    <div className="col-span-9 flex items-center justify-center">
                      <img 
                        src="/uploads/dental-chart-2.png" 
                        alt="Child Dental Chart" 
                        className="h-[315px] object-contain"
                        onLoad={() => console.log('Dental chart 2 loaded in print')}
                        onError={() => console.warn('Dental chart 2 failed to load in print')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseSheetPrint;